import { useState, useEffect, createContext, useContext, ReactNode } from "react";

interface CurrencyContextType {
  currency: "INR" | "USD";
  symbol: string;
  convert: (inrAmount: number) => number;
  formatPrice: (inrAmount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: "INR",
  symbol: "₹",
  convert: (v) => v,
  formatPrice: (v) => `₹${v.toLocaleString("en-IN")}`,
});

const INR_TO_USD = 0.012; // ~1 USD = 83 INR

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");

  useEffect(() => {
    const detectLocation = async () => {
      try {
        const cached = sessionStorage.getItem("user_country");
        if (cached) {
          if (cached !== "IN") setCurrency("USD");
          return;
        }
        const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(4000) });
        if (!res.ok) return;
        const data = await res.json();
        if (data.country_code) {
          sessionStorage.setItem("user_country", data.country_code);
          if (data.country_code !== "IN") setCurrency("USD");
        }
      } catch {
        // Default to INR on failure
      }
    };
    detectLocation();
  }, []);

  const symbol = currency === "INR" ? "₹" : "$";

  const convert = (inrAmount: number): number => {
    if (currency === "INR") return inrAmount;
    return Math.round(inrAmount * INR_TO_USD);
  };

  const formatPrice = (inrAmount: number): string => {
    const amount = convert(inrAmount);
    if (currency === "INR") {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
    return `$${amount.toLocaleString("en-US")}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, symbol, convert, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
